import { formatReportSmartDateTime } from "@/utility/fclReportHelpers";
import { cell, pickField } from "@/utility/fclReportFieldUtils";

export const EMPTY = <span className="text-gray-400 dark:text-gray-500">-</span>;

const TRUNCATE_CLASS = "block min-w-0 max-w-full truncate";

export const clipText = (value, extraClass = "") => {
    const text = cell(value);
    if (text === "-") return EMPTY;
    return (
        <span className={`${TRUNCATE_CLASS} text-[13px] ${extraClass}`} title={text}>
            {text}
        </span>
    );
};

/** Date cell with truncate + native tooltip for overflow text. */
export const reportDateCell = (value) => {
    if (!value) return EMPTY;
    const formatted = formatReportSmartDateTime(value, "");
    if (!formatted) return EMPTY;
    return (
        <span className={`${TRUNCATE_CLASS} tabular-nums text-[12px]`} title={formatted}>
            {formatted}
        </span>
    );
};

export const pickSort = (row, keys) => String(pickField(row, keys, "")).toLowerCase();

export const pickDateSort = (row, keys) => {
    const v = pickField(row, keys, null);
    if (!v) return 0;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
};

export const reportCol = ({
    key,
    label,
    render,
    sortValue,
    getTooltip,
    width,
    sticky,
    align = "left",
}) => ({
    key,
    label,
    width,
    sticky,
    render,
    sortable: true,
    ellipsis: true,
    sortValue: sortValue ?? ((row) => String(render(row) ?? "").toLowerCase()),
    getTooltip:
        getTooltip ??
        (sortValue
            ? (row) => {
                  const v = sortValue(row);
                  if (typeof v === "number") return "";
                  const s = String(v ?? "");
                  return s && s !== "-" ? s : "";
              }
            : undefined),
    tdClass: align === "center" ? "text-center" : align === "right" ? "text-right" : "",
    thClass: align === "center" ? "text-center" : align === "right" ? "text-right" : "",
});

/** Text column with truncate + tooltip — matches import booking report cells. */
export const textReportCol = ({ key, label, fieldKeys, width, sticky, align }) =>
    reportCol({
        key,
        label,
        width,
        sticky,
        align,
        render: (row) => clipText(pickField(row, fieldKeys)),
        sortValue: (row) => pickSort(row, fieldKeys),
    });

/** Column config for date fields — tooltip shows full formatted date/time. */
export const dateReportCol = ({ key, label, fieldKeys, width, sticky, align }) =>
    reportCol({
        key,
        label,
        width,
        sticky,
        align,
        render: (row) => reportDateCell(pickField(row, fieldKeys, null)),
        getTooltip: (row) => {
            const v = pickField(row, fieldKeys, null);
            return v ? formatReportSmartDateTime(v, "") : "";
        },
        sortValue: (row) => pickDateSort(row, fieldKeys),
    });

/** Blue link-style file reference with tooltip. */
export const fileRefLinkCell = (ref) => {
    if (!ref) return EMPTY;
    const text = String(ref).trim();
    return (
        <span
            className={`${TRUNCATE_CLASS} font-medium text-blue-600 underline decoration-blue-400/50 dark:text-blue-400`}
            title={text}
        >
            {text}
        </span>
    );
};
